import { styled } from "@mui/material/styles";
import {
  BlockTypeSelect,
  BoldItalicUnderlineToggles,
  codeBlockPlugin,
  CodeToggle,
  CreateLink,
  directivesPlugin,
  frontmatterPlugin,
  headingsPlugin,
  imagePlugin,
  InsertCodeBlock,
  InsertImage,
  InsertTable,
  InsertThematicBreak,
  jsxPlugin,
  linkDialogPlugin,
  linkPlugin,
  listsPlugin,
  ListsToggle,
  markdownShortcutPlugin,
  MDXEditor,
  type MDXEditorMethods,
  quotePlugin,
  Separator,
  tablePlugin,
  thematicBreakPlugin,
  toolbarPlugin,
  UndoRedo,
} from "@mdxeditor/editor";

import "@mdxeditor/editor/style.css";
import { UploadImageMutation } from "@/shared/graphql";
import { useMutation } from "@apollo/client";
import { useRef, useEffect } from "react";
import type {
  UploadImageMutationType,
  UploadImageMutationVariablesType,
} from "@/shared/types/common";

const Root = styled("div")(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  maxHeight: 600,
  overflow: "hidden",
  overflowY: "auto",
  backgroundColor: theme.palette.background.paper,

  ...theme.applyStyles("dark", {
    border: "1px solid rgba(255, 255, 255, 0.23)",
  }),
  ...theme.applyStyles("light", {
    border: "1px solid rgba(0, 0, 0, 0.23)",
  }),
  "&:hover": {
    ...theme.applyStyles("dark", {
      borderColor: "rgba(255, 255, 255, 1)",
    }),
    ...theme.applyStyles("light", {
      borderColor: "rgba(0, 0, 0, 1)",
    }),
  },

  "& .mdxeditor-toolbar": {
    borderRadius: 0,
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.primary,
    "& button": {
      color: "inherit",
      borderRadius: theme.shape.borderRadius,
      "& svg": {
        color: "inherit",
        fill: "currentColor",
      },
      "&:hover": {
        backgroundColor: theme.palette.action.hover,
      },
      "&[data-active='true']": {
        backgroundColor: theme.palette.action.selected,
      },
    },
    "& button[role='combobox'], & ._selectTrigger_1e2ox_308": {
      backgroundColor: theme.palette.background.default,
      color: theme.palette.text.primary,
      border: `1px solid ${theme.palette.divider}`,
      "& span, & svg": {
        color: "inherit",
        fill: "currentColor",
      },
    },
    "& select, & input": {
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.background.default,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
    },
  },

  "& .editor": {
    color: theme.palette.text.primary,
    fontFamily: theme.typography.fontFamily,
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,

    // Заголовки
    "& h1": {
      ...theme.typography.h1,
      color: theme.palette.text.primary,
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(1),
    },
    "& h2": {
      ...theme.typography.h2,
      color: theme.palette.text.primary,
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(1),
    },
    "& h3": {
      ...theme.typography.h3,
      color: theme.palette.text.primary,
      marginTop: theme.spacing(1.5),
      marginBottom: theme.spacing(1),
    },
    "& h4": {
      ...theme.typography.h4,
      color: theme.palette.text.primary,
      marginTop: theme.spacing(1.5),
      marginBottom: theme.spacing(0.5),
    },
    "& h5": {
      ...theme.typography.h5,
      color: theme.palette.text.primary,
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(0.5),
    },
    "& h6": {
      ...theme.typography.h6,
      color: theme.palette.text.primary,
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(0.5),
    },

    // Основной текст
    "& p": {
      ...theme.typography.body1,
      color: theme.palette.text.primary,
      marginTop: 0,
      marginBottom: theme.spacing(1),
    },

    // Списки
    "& ul, & ol": {
      ...theme.typography.body1,
      color: theme.palette.text.primary,
      paddingLeft: theme.spacing(3),
      marginTop: theme.spacing(0.5),
      marginBottom: theme.spacing(1),
    },
    "& li": {
      ...theme.typography.body1,
      color: theme.palette.text.primary,
      marginBottom: theme.spacing(0.5),
    },

    // Цитаты
    "& blockquote": {
      ...theme.typography.body1,
      color: theme.palette.text.secondary,
      fontStyle: "italic",
      paddingLeft: theme.spacing(2),
      borderLeft: `4px solid ${theme.palette.primary.main}`,
      marginLeft: 0,
      marginRight: 0,
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
      backgroundColor: theme.palette.action.hover,
      padding: theme.spacing(1, 2),
      borderRadius: theme.shape.borderRadius,
    },

    // Код
    "& code": {
      ...theme.typography.body2,
      fontFamily: theme.typography.fontFamily,
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.text.primary,
      padding: theme.spacing(0.25, 0.5),
      borderRadius: theme.shape.borderRadius,
      fontSize: "0.875em",
    },
    "& pre": {
      ...theme.typography.body2,
      fontFamily: theme.typography.fontFamily,
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.text.primary,
      padding: theme.spacing(1.5),
      borderRadius: theme.shape.borderRadius,
      overflow: "auto",
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
    "& pre code": {
      backgroundColor: "transparent",
      padding: 0,
    },

    // Ссылки
    "& a": {
      color: theme.palette.primary.main,
      textDecoration: "none",
      "&:hover": {
        textDecoration: "underline",
      },
    },

    // Горизонтальная линия
    "& hr": {
      border: "none",
      borderTop: `1px solid ${theme.palette.divider}`,
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },

    // Таблицы
    "& table": {
      width: "100%",
      borderCollapse: "collapse",
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
      border: `1px solid ${theme.palette.divider}`,
    },
    "& th, & td": {
      ...theme.typography.body2,
      color: theme.palette.text.primary,
      border: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing(1, 2),
      textAlign: "left",
    },
    "& th": {
      backgroundColor: theme.palette.action.hover,
      fontWeight: theme.typography.fontWeightMedium,
    },
    "& tbody tr:nth-of-type(even)": {
      backgroundColor: theme.palette.action.hover,
    },

    // Блоки кода с подсветкой
    "& .language-javascript, & .language-html, & .language-css, & .language-json":
      {
        backgroundColor: `${theme.palette.grey[900]} !important`,
        color: `${theme.palette.common.white} !important`,
        ...theme.applyStyles("light", {
          backgroundColor: `${theme.palette.grey[100]} !important`,
          color: `${theme.palette.text.primary} !important`,
        }),
      },

    // Инлайн код
    "& :not(pre) > code": {
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.primary.main,
      fontWeight: theme.typography.fontWeightMedium,
    },

    // Директивы и специальные блоки
    "& .directive": {
      backgroundColor: theme.palette.action.hover,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      padding: theme.spacing(1),
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },

    // Первый элемент без отступа сверху
    "& > *:first-of-type": {
      marginTop: 0,
    },
    // Последний элемент без отступа снизу
    "& > *:last-child": {
      marginBottom: 0,
    },
  },
}));

interface MarkdownInputProps {
  value?: string;
  onChange: (value: string) => void;
  /** Режим «только ссылки» (референсный MarkdownInput.js linksOnly):
   * минимальный набор плагинов — UndoRedo + CreateLink, без заголовков,
   * списков, картинок и таблиц. Для коротких текстов вроде аннотаций. */
  linksOnly?: boolean;
}

export function MarkdownInput({
  value,
  onChange,
  linksOnly = false,
}: MarkdownInputProps) {
  const [uploadImage] = useMutation<
    UploadImageMutationType,
    UploadImageMutationVariablesType
  >(UploadImageMutation);

  const refEditor = useRef<MDXEditorMethods>(null);

  useEffect(() => {
    if (refEditor.current) {
      refEditor.current.setMarkdown(value || "");
    }
  }, [value]);

  const linksOnlyPlugins = [
    linkPlugin(),
    linkDialogPlugin(),
    toolbarPlugin({
      toolbarContents: () => (
        <>
          <UndoRedo />
          <CreateLink />
        </>
      ),
    }),
  ];

  return (
    <Root>
      <MDXEditor
        ref={refEditor}
        markdown={value || ""}
        onChange={onChange}
        contentEditableClassName="editor"
        onError={(error: any) => console.error("onError", error)}
        plugins={
          linksOnly
            ? linksOnlyPlugins
            : [
                // Основные плагины
                headingsPlugin(),
                listsPlugin(),
                quotePlugin(),
                thematicBreakPlugin(),
                // Поддержка кода
                codeBlockPlugin({
                  defaultCodeBlockLanguage: "javascript",
                }),
                // Поддержка таблиц
                tablePlugin(),
                // Поддержка JSX/HTML
                jsxPlugin(),
                // Директивы для расширенной функциональности
                directivesPlugin(),
                // Фронтматтер (метаданные)
                frontmatterPlugin(),
                // Markdown шорткаты
                markdownShortcutPlugin(),
                // Изображения с загрузкой
                imagePlugin({
                  imageUploadHandler: async (file: File) => {
                    const result = await uploadImage({
                      variables: {
                        input: {
                          file,
                        },
                      },
                    });

                    return result.data?.file.url || "";
                  },
                }),
                // Тулбар
                toolbarPlugin({
                  toolbarContents: () => (
                    <>
                      <UndoRedo />
                      <Separator />
                      <BlockTypeSelect />
                      <BoldItalicUnderlineToggles />
                      <CodeToggle />
                      <Separator />
                      <ListsToggle />
                      <Separator />
                      <CreateLink />
                      <InsertImage />
                      <InsertTable />
                      <InsertThematicBreak />
                      <InsertCodeBlock />
                    </>
                  ),
                }),
                // Ссылки
                linkPlugin(),
                linkDialogPlugin(),
              ]
        }
      />
    </Root>
  );
}
